import { Grid } from '@mui/material';
import { Dialog, ConfirmationDialog } from '@components/dialog';
import { DatePicker } from '@components/datePicker';
import { ArrayItem } from '@utils/commonTypes';
import { QuoteGetResponse } from '@lio/api-types';
import { QuoteStatus } from '@models/quotes';
import { useEffect, useState } from 'react';
import { DateValidationError } from '@mui/x-date-pickers';
import { addDays } from 'date-fns';

type QuoteStatusType = ArrayItem<QuoteGetResponse>['status'];

export const ReactivateExpiredCancelledModal = ({
  isOpen,
  onCancel,
  onConfirm,
  policyNumber,
  isLoading,
  quoteStatus,
  effectiveOnDate,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (date: Date | null) => void;
  policyNumber?: string;
  isLoading: boolean;
  quoteStatus?: QuoteStatusType;
  effectiveOnDate?: Date;
}) => {
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(effectiveOnDate ?? null);
  const [hasEffectiveDateError, setHasEffectiveDateError] = useState<DateValidationError | null>(null);
  const isExpiredQuote = quoteStatus?.id === QuoteStatus.Expired.id;
  const isCanceledQuote = quoteStatus?.id === QuoteStatus.Canceled.id;

  const hasPropsEffectiveOnDate = !!effectiveOnDate;
  const isDatePickerEffectiveDateInvalid = !effectiveDate || !!hasEffectiveDateError;
  const isConfirmButtonDisabled = (!hasPropsEffectiveOnDate && isDatePickerEffectiveDateInvalid) || isLoading;

  useEffect(() => {
    if (isOpen) {
      if (quoteStatus?.id === QuoteStatus.Expired.id) {
        setEffectiveDate(effectiveOnDate ? effectiveOnDate : new Date());
      } else if (quoteStatus?.id === QuoteStatus.Canceled.id) {
        setEffectiveDate(new Date());
      }
      setHasEffectiveDateError(null);
    }
  }, [effectiveOnDate, isOpen, quoteStatus?.id]);

  const handleFieldChange = (value: Date | null) => {
    setEffectiveDate(value || null);
  };

  const handleConfirmDialog = () => {
    onConfirm(effectiveDate);
    setEffectiveDate(null);
  };

  const handleExitDialog = () => {
    onCancel();
  };

  return isExpiredQuote || isCanceledQuote ? (
    <Dialog
      open={isOpen}
      title={`Reactivation of ${quoteStatus?.label} Quote #${policyNumber}`}
      handleCloseDialog={handleExitDialog}
      isCloseButtonDisabled={isLoading}
      primaryBtnText='Reactivate'
      primaryBtnAction={handleConfirmDialog}
      isPrimaryBtnDisabled={isConfirmButtonDisabled}
      isPrimaryBtnLoading={isLoading}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleExitDialog}
      isSecondaryBtnDisabled={isLoading}
    >
      <Grid
        container
        sx={{
          alignItems: 'left',
          justifyContent: 'center',
        }}
      >
        <Grid
          size={{ xs: 11 }}
          sx={{
            padding: '24px 0',
          }}
        >
          <DatePicker
            value={effectiveDate}
            onChange={handleFieldChange}
            textFieldProps={{
              inputLabel: isCanceledQuote ? 'New Policy Effective Date' : 'Effective Date',
              placeholder: 'MM/DD/YYYY',
            }}
            minDate={hasPropsEffectiveOnDate || isCanceledQuote ? new Date() : undefined}
            maxDate={hasPropsEffectiveOnDate ? addDays(new Date(), 120) : undefined}
            onError={setHasEffectiveDateError}
            disabled={hasPropsEffectiveOnDate}
          />
        </Grid>
      </Grid>
    </Dialog>
  ) : (
    <ConfirmationDialog
      isOpen={isOpen}
      onCancel={handleExitDialog}
      description='Are you sure you want to reactivate this quote?'
      buttonConfig={{
        confirmText: 'Reactivate',
        onConfirm: handleConfirmDialog,
        secondaryText: 'Exit',
      }}
    />
  );
};
